import type { InspectorContext, InspectorEvent, InspectorStateValue } from './protocol';
import type { VoiceLiveContext, VoiceLiveEvent } from '../machine/types';

const MAX_DIAGNOSTIC_TEXT_LENGTH = 240;

const VOICE_LIVE_EVENT_TYPES: Record<VoiceLiveEvent['type'], true> = {
  START: true,
  STOP: true,
  MANUAL_RETRY: true,
  WS_OPEN: true,
  WS_CLOSE: true,
  WS_ERROR: true,
  MIC_CAPTURE_FAILED: true,
  SESSION_UPDATED: true,
  ICE_SERVERS: true,
  FIELDONE_SESSION_CREATED: true,
  AVATAR_ACTIVE: true,
  AVATAR_FAILED: true,
  REATTEMPT: true,
  SWITCH_TO_AUDIO: true,
  ONLINE: true,
  OFFLINE: true,
  SET_ERROR: true,
  SET_VIDEO_ENABLED: true,
  RECONNECT_SESSION: true,
};

const truncateDiagnosticText = (value: string) =>
  value.length > MAX_DIAGNOSTIC_TEXT_LENGTH
    ? `${value.slice(0, MAX_DIAGNOSTIC_TEXT_LENGTH - 3)}...`
    : value;

const getIceServerSummary = (servers: RTCIceServer[]) => {
  const urls = servers.flatMap((server) =>
    Array.isArray(server.urls) ? server.urls : [server.urls],
  );

  return {
    hasTurnServer: urls.some((url) => /^turns?:/i.test(url)),
    iceServerCount: servers.length,
  };
};

export const sanitizeVoiceLiveContext = (context: VoiceLiveContext): InspectorContext => ({
  avatarEverActive: context.avatarEverActive,
  degradedToAudio: context.degradedToAudio,
  errorMessage: truncateDiagnosticText(context.errorMessage),
  hasIceServers: context.hasIceServers,
  ...getIceServerSummary(context.iceServers),
  initialAvatarFailures: context.initialAvatarFailures,
  socketIsAudioOnly: context.socketIsAudioOnly,
  isVideoEnabled: context.isVideoEnabled,
  retryCount: context.retryCount,
  sessionReady: context.sessionReady,
  shouldReconnect: context.shouldReconnect,
});

export const sanitizeVoiceLiveEvent = (event: VoiceLiveEvent): InspectorEvent => {
  switch (event.type) {
    case 'WS_CLOSE':
      return { type: event.type, details: { code: event.code } };
    case 'MIC_CAPTURE_FAILED':
    case 'SET_ERROR':
      return {
        type: event.type,
        details: { message: truncateDiagnosticText(event.message) },
      };
    case 'ICE_SERVERS':
      return { type: event.type, details: getIceServerSummary(event.servers) };
    case 'FIELDONE_SESSION_CREATED':
      return { type: event.type, details: { sessionCreated: true } };
    case 'AVATAR_FAILED':
      return {
        type: event.type,
        details: { reason: truncateDiagnosticText(event.reason) },
      };
    case 'SET_VIDEO_ENABLED':
      return { type: event.type, details: { enabled: event.enabled } };
    case 'START':
    case 'STOP':
    case 'MANUAL_RETRY':
    case 'WS_OPEN':
    case 'WS_ERROR':
    case 'SESSION_UPDATED':
    case 'AVATAR_ACTIVE':
    case 'REATTEMPT':
    case 'SWITCH_TO_AUDIO':
    case 'ONLINE':
    case 'OFFLINE':
    case 'RECONNECT_SESSION':
      return { type: event.type };
    default:
      return assertNever(event);
  }
};

export const sanitizeInspectionEvent = (event: { type: string }): InspectorEvent => {
  // hasOwn, not `in`: prototype keys like 'constructor' would reach assertNever and throw.
  if (Object.hasOwn(VOICE_LIVE_EVENT_TYPES, event.type)) {
    return sanitizeVoiceLiveEvent(event as VoiceLiveEvent);
  }

  return { type: truncateDiagnosticText(event.type) };
};

export const sanitizeStateValue = (value: unknown): InspectorStateValue => {
  if (typeof value === 'string') return value;

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, sanitizeStateValue(nestedValue)]),
    );
  }

  return 'unknown';
};

const assertNever = (value: never): never => {
  throw new Error(`Unsupported Voice Live event: ${JSON.stringify(value)}`);
};
